import { Check, Star } from "lucide-react";
import { SITE } from "@/lib/site";

const PLANS = [
  {
    id: "essentielle",
    name: "Essentielle",
    tagline: "Pour démarrer sereinement",
    price: 290,
    priceFirst: 199,
    features: [
      "Rédaction et envoi de devis",
      "Facturation client",
      "Relances impayés (2 niveaux)",
      "Classement et archivage des documents",
      "Point hebdomadaire par message",
    ],
    recommended: false,
    cta: "Je choisis Essentielle",
  },
  {
    id: "serenite",
    name: "Sérénité",
    tagline: "Pour tout déléguer",
    price: 490,
    priceFirst: 340,
    features: [
      "Tout de la formule Essentielle",
      "Suivi administratif complet (URSSAF, assurances, courriers)",
      "Relances clients automatisées",
      "Gestion des retards de paiement",
      "Automatisations sur-mesure (modèles, envois récurrents)",
      "Disponibilité prioritaire",
    ],
    recommended: true,
    cta: "Je choisis Sérénité",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="bg-[#1A2E4A] py-20 sm:py-28 lg:py-32 relative overflow-hidden"
    >
      <div className="absolute inset-0 grain-overlay opacity-25 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl reveal">
          <span className="inline-block text-[#E8611A] font-semibold text-sm tracking-widest uppercase mb-4">
            Les formules
          </span>
          <h2 className="font-display font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Choisissez votre formule
          </h2>
          <p className="mt-4 text-white/65 text-base sm:text-lg">
            Sans engagement. Résiliation avec 15 jours de préavis.
          </p>
        </div>

        <div className="mt-14 sm:mt-20 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
          {PLANS.map((p, i) => (
            <article
              key={p.id}
              data-testid={`plan-${p.id}`}
              className={`reveal relative flex flex-col rounded-sm p-8 sm:p-10 transition-all duration-300 hover:-translate-y-1 ${
                p.recommended
                  ? "bg-white text-[#1A2E4A] border-2 border-[#E8611A] shadow-2xl shadow-black/30 lg:scale-[1.02]"
                  : "bg-white/5 text-white border border-white/15 backdrop-blur-sm"
              }`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              {p.recommended && (
                <span
                  data-testid="plan-badge-recommended"
                  className="absolute -top-3 left-8 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-[#E8611A] text-white text-xs font-bold uppercase tracking-wider"
                >
                  <Star className="w-3.5 h-3.5 fill-white" strokeWidth={0} />
                  Recommandée
                </span>
              )}

              <div>
                <h3 className="font-display font-extrabold text-3xl sm:text-4xl">
                  {p.name}
                </h3>
                <p
                  className={`mt-1 text-sm ${
                    p.recommended ? "text-[#8A8A8A]" : "text-white/55"
                  }`}
                >
                  {p.tagline}
                </p>
              </div>

              <div className="mt-7 pb-7 border-b border-dashed border-current/15">
                <div className="flex items-baseline gap-2">
                  <span className="font-display font-extrabold text-5xl sm:text-6xl">
                    {p.priceFirst}€
                  </span>
                  <span
                    className={`text-sm ${
                      p.recommended ? "text-[#8A8A8A]" : "text-white/55"
                    }`}
                  >
                    le 1er mois
                  </span>
                </div>
                <div
                  className={`mt-2 text-sm ${
                    p.recommended ? "text-[#8A8A8A]" : "text-white/55"
                  }`}
                >
                  puis{" "}
                  <span
                    className={`font-semibold ${
                      p.recommended ? "text-[#1A2E4A]" : "text-white"
                    }`}
                  >
                    {p.price}€/mois
                  </span>{" "}
                  · sans engagement
                </div>
              </div>

              <ul className="mt-7 space-y-3.5 flex-1">
                {p.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-3">
                    <span
                      className={`shrink-0 mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-sm ${
                        p.recommended
                          ? "bg-[#E8611A]/10 text-[#E8611A]"
                          : "bg-[#E8611A]/20 text-[#E8611A]"
                      }`}
                    >
                      <Check className="w-3.5 h-3.5" strokeWidth={3} />
                    </span>
                    <span
                      className={`text-sm sm:text-base ${
                        p.recommended ? "text-[#2D2D2D]" : "text-white/85"
                      }`}
                    >
                      {f}
                    </span>
                  </li>
                ))}
              </ul>

              <a
                href={SITE.calendly}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`plan-cta-${p.id}`}
                className={`mt-8 inline-flex items-center justify-center px-5 py-3.5 rounded-sm font-semibold transition-colors ${
                  p.recommended
                    ? "bg-[#E8611A] text-white hover:bg-[#E8611A]/90"
                    : "bg-white text-[#1A2E4A] hover:bg-white/90"
                }`}
              >
                {p.cta}
              </a>
            </article>
          ))}
        </div>

        <p className="mt-10 text-center text-white/55 text-xs sm:text-sm max-w-2xl mx-auto">
          Toutes nos formules sont 100% déductibles de vos charges
          professionnelles. Un artisan en régime réel récupère entre 150€ et
          230€ par mois via la déduction fiscale.
        </p>
      </div>
    </section>
  );
}
